import React, { useEffect, useRef } from 'react';
import { ArrowRight, BarChart3, ShieldCheck, Sparkles, Target, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { siteContent } from '../content/siteContent';

const trustIconMap = {
  zap: Zap,
  target: Target,
  barChart3: BarChart3,
  shieldCheck: ShieldCheck,
};

const Hero = () => {
  const heroRef = useRef(null);
  const { hero } = siteContent;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = heroRef.current?.querySelectorAll('.fade-in-up');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const scrollToContact = (e) => {
    e.preventDefault();
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToWork = (e) => {
    e.preventDefault();
    document.querySelector('#work')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#D22527] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      <div className="absolute top-1/3 -right-32 w-96 h-96 bg-[#88171A] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-[#D22527] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="text-center">
          <div className="fade-in-up inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D22527]/10 border border-[#D22527]/30 backdrop-blur-sm mb-8">
            <Sparkles className="w-4 h-4 text-[#D22527]" />
            <span className="text-sm text-gray-300">{hero.badge}</span>
          </div>

          <h1 className="fade-in-up text-5xl md:text-7xl lg:text-8xl font-bold mb-8 leading-tight">
            <span className="text-white">{hero.headline[0]}</span>
            <br />
            <span className="bg-gradient-to-r from-[#D22527] to-[#88171A] bg-clip-text text-transparent">
              {hero.headline[1]}
            </span>
            <br />
            <span className="text-white">{hero.headline[2]}</span>
          </h1>

          <p className="fade-in-up text-xl md:text-2xl text-gray-400 max-w-4xl mx-auto mb-12 leading-relaxed">
            {hero.subheadline}
          </p>

          <div className="fade-in-up flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              onClick={scrollToContact}
              className="group bg-gradient-to-r from-[#D22527] to-[#88171A] text-white text-lg px-8 py-6 hover:shadow-2xl hover:shadow-[#D22527]/50 transition-all duration-500 hover:scale-105 border-0"
            >
              {hero.primaryCta}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
            </Button>
            <Button
              onClick={scrollToWork}
              variant="outline"
              className="text-lg px-8 py-6 bg-transparent border-2 border-[#D22527]/50 text-white hover:bg-[#D22527]/10 hover:border-[#D22527] transition-all duration-300"
            >
              {hero.secondaryCta}
            </Button>
          </div>

          <p className="fade-in-up mt-4 text-sm md:text-base text-gray-500">{hero.reassurance}</p>

          <div className="fade-in-up mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {hero.trustIndicators.map((item, index) => {
              const Icon = trustIconMap[item.icon];
              return (
                <div
                  key={index}
                  className="group p-6 rounded-2xl bg-gradient-to-br from-[#D22527]/5 to-transparent border border-[#D22527]/20 hover:border-[#D22527]/50 transition-all duration-500 hover:scale-105 backdrop-blur-sm"
                >
                  <div className="mb-3 flex justify-center">
                    <div className="w-12 h-12 rounded-2xl bg-[#D22527]/10 border border-[#D22527]/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-6 h-6 text-[#D22527]" />
                    </div>
                  </div>
                  <p className="text-sm text-gray-400 group-hover:text-white transition-colors duration-300">{item.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-[#D22527]/50 flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-[#D22527] rounded-full animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
